import { useState, useCallback, useRef } from 'react';
import type { Message } from '../types/events';

/**
 * Options for the useMessages hook.
 */
export interface UseMessagesOptions {
  /** Maximum number of messages to keep in history */
  maxMessages?: number;
}

/**
 * Return type for the useMessages hook.
 */
export interface UseMessagesReturn {
  /** Array of all messages */
  messages: Message[];
  /** Whether assistant typing indicator should be shown */
  isAssistantTyping: boolean;
  /** Whether there's a message currently streaming */
  isStreaming: boolean;
  /** ID of the currently streaming message, if any */
  streamingMessageId: string | null;
  /** Add a user message */
  addUserMessage: (content: string) => Message;
  /** Add a bot/assistant message (non-streaming) */
  addBotMessage: (id: string, content: string, metadata?: Record<string, unknown>) => Message;
  /** Add a system message */
  addSystemMessage: (content: string) => Message;
  /** Start a new streaming message */
  startStreamingMessage: (messageId: string) => void;
  /** Append content to the currently streaming message */
  appendStreamingChunk: (messageId: string, chunk: string) => void;
  /** Finalize the streaming message */
  finalizeStreamingMessage: (messageId: string, content: string, metadata?: Record<string, unknown>) => void;
  /** Set the full message history (for session restoration) */
  setMessageHistory: (messages: Message[]) => void;
  /** Toggle assistant typing indicator */
  setAssistantTyping: (isTyping: boolean) => void;
  /** Clear all messages */
  clearMessages: () => void;
}

/**
 * Generate a unique message ID.
 */
function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Hook for managing chat message state.
 *
 * Handles adding messages from users and the bot, streaming message
 * accumulation, and message history management.
 *
 * @param options - Configuration options
 * @returns Message state and manipulation methods
 */
export function useMessages(options: UseMessagesOptions = {}): UseMessagesReturn {
  const { maxMessages = 1000 } = options;

  const [messages, setMessages] = useState<Message[]>([]);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);

  // Use ref to track streaming content for efficient accumulation
  const streamingContentRef = useRef<string>('');

  /**
   * Add a message to the state, respecting max message limit.
   */
  const addMessage = useCallback(
    (message: Message): Message => {
      setMessages((prev) => {
        const updated = [...prev, message];
        // Trim to max messages if needed
        if (updated.length > maxMessages) {
          return updated.slice(updated.length - maxMessages);
        }
        return updated;
      });
      return message;
    },
    [maxMessages]
  );

  /**
   * Add a user message.
   */
  const addUserMessage = useCallback(
    (content: string): Message => {
      const message: Message = {
        id: generateMessageId(),
        content,
        senderType: 'user',
        timestamp: new Date(),
        isStreaming: false,
      };
      return addMessage(message);
    },
    [addMessage]
  );

  /**
   * Add a bot/assistant message (non-streaming).
   */
  const addBotMessage = useCallback(
    (id: string, content: string, metadata?: Record<string, unknown>): Message => {
      const message: Message = {
        id,
        content,
        senderType: 'assistant',
        timestamp: new Date(),
        isStreaming: false,
        metadata,
      };
      return addMessage(message);
    },
    [addMessage]
  );

  /**
   * Add a system message.
   */
  const addSystemMessage = useCallback(
    (content: string): Message => {
      const message: Message = {
        id: generateMessageId(),
        content,
        senderType: 'system',
        timestamp: new Date(),
        isStreaming: false,
      };
      return addMessage(message);
    },
    [addMessage]
  );

  /**
   * Start a new streaming message.
   * Creates a placeholder message that will be updated with chunks.
   */
  const startStreamingMessage = useCallback((messageId: string) => {
    streamingContentRef.current = '';
    setStreamingMessageId(messageId);

    const message: Message = {
      id: messageId,
      content: '',
      senderType: 'assistant',
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, message]);
  }, []);

  /**
   * Append content to the currently streaming message.
   */
  const appendStreamingChunk = useCallback(
    (messageId: string, chunk: string) => {
      // Accumulate in ref for efficiency
      streamingContentRef.current += chunk;
      const newContent = streamingContentRef.current;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? { ...msg, content: newContent }
            : msg
        )
      );
    },
    []
  );

  /**
   * Finalize the streaming message.
   * Updates the message with final content and marks it as complete.
   */
  const finalizeStreamingMessage = useCallback(
    (messageId: string, content: string, metadata?: Record<string, unknown>) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                content,
                isStreaming: false,
                metadata,
              }
            : msg
        )
      );

      streamingContentRef.current = '';
      setStreamingMessageId(null);
    },
    []
  );

  /**
   * Set the full message history (used for session restoration).
   */
  const setMessageHistory = useCallback(
    (history: Message[]) => {
      // Ensure all messages have proper Date objects
      const normalizedHistory = history.map((msg) => ({
        ...msg,
        timestamp: msg.timestamp instanceof Date
          ? msg.timestamp
          : new Date(msg.timestamp),
        isStreaming: false,
      }));

      // Trim to max messages if needed
      if (normalizedHistory.length > maxMessages) {
        setMessages(normalizedHistory.slice(normalizedHistory.length - maxMessages));
      } else {
        setMessages(normalizedHistory);
      }
    },
    [maxMessages]
  );

  /**
   * Clear all messages.
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setStreamingMessageId(null);
    setIsAssistantTyping(false);
    streamingContentRef.current = '';
  }, []);

  return {
    messages,
    isAssistantTyping,
    isStreaming: streamingMessageId !== null,
    streamingMessageId,
    addUserMessage,
    addBotMessage,
    addSystemMessage,
    startStreamingMessage,
    appendStreamingChunk,
    finalizeStreamingMessage,
    setMessageHistory,
    setAssistantTyping: setIsAssistantTyping,
    clearMessages,
  };
}

export default useMessages;
